/**
 * CUSTOM 2026-08-14 — <hl-pack-picker>, the quantity-tier + purchase-option
 * picker for the PDP buy box. Markup, CSS and all price strings come from
 * blocks/hl-pack-picker.liquid. Mechanics: docs/bundle-pack-mechanics.md.
 *
 * WHAT THIS SCRIPT DOES NOT DO: money arithmetic. Every (quantity x purchase
 * option) price is precomputed server-side with Shopify's `money` filter and
 * shipped in a <script type="application/json"> blob. This element only looks
 * up prepared strings and writes them into the DOM. That keeps the buy box
 * correct in NOK today and in any other currency or market without a code
 * change, and sidesteps the float-rounding class of bug entirely — an exact
 * 10% saving computed in JS lands on 0.09999999999999993 and displays as "9%".
 *
 * WHAT IT WRITES TO THE CART FORM
 *   quantity      -> the selected pack size, into Horizon's real
 *                    input[name="quantity"] (rendered by the nested `quantity`
 *                    block inside buy-buttons). The pack ladder is the only
 *                    quantity control on the page; the stepper is hidden by
 *                    CSS, not removed, so Horizon's own quantity-rule
 *                    validation keeps operating on the same input.
 *   selling_plan  -> the chosen plan id, or "" for a one-time purchase.
 *
 * The pack DISCOUNT is not written anywhere — it is applied at the cart by
 * Bundler's discount function based on line quantity. This element's job is to
 * make sure the quantity that reaches the cart is the one the shopper was
 * quoted.
 *
 * UPSTREAM risk: MEDIUM. Depends on the buy-buttons form id
 * (`BuyButtons-ProductForm-<section.id>`) and on an input[name="quantity"]
 * existing inside it. Both are checked at runtime with a fallback, so a
 * Horizon change degrades to a working add-to-cart rather than a wrong one.
 */

class HlPackPicker extends HTMLElement {
  connectedCallback() {
    if (this.dataset.mounted === 'true') return;
    this.dataset.mounted = 'true';

    const raw = this.querySelector('[data-pack-prices]');
    try {
      /** @type {Array<{qty:number,plan:string,total:string,unit:string,gross:string,save:string,pct:number,extra:string}>} */
      this.prices = JSON.parse(raw?.textContent || '[]');
    } catch (error) {
      // A malformed blob must not take the add-to-cart button down with it.
      console.warn('[hl-pack-picker] could not parse price data:', error);
      this.prices = [];
    }

    // Every allocation with the rule (selling plan group) it belongs to.
    // Used to rebuild the delivery-frequency list for whichever pack size is
    // selected, once each rule carries several frequencies.
    const planData = this.querySelector('[data-pack-plans]');
    try {
      this.plans = planData ? JSON.parse(planData.textContent || '[]') : [];
    } catch (error) {
      console.warn('[hl-pack-picker] could not parse plan data:', error);
      this.plans = [];
    }

    // Bounds for the open-ended top tier. maxQty exists because every price is
    // prepared server-side — the stepper can only reach quantities that have a
    // prepared row, which is what keeps money arithmetic out of this file.
    this.topTierQty = parseInt(this.dataset.topTierQty, 10) || 1;
    this.maxQty = parseInt(this.dataset.maxQty, 10) || this.topTierQty;

    // The market price in subunits. Used for exactly one thing: redoing
    // Bundler's float sum to decide which of two prepared strings to show on an
    // exact-half-cent row. See #row. It never produces a price.
    this.unitCents = parseInt(this.dataset.unitCents, 10) || 0;
    // False when only one tier survived, in which case the cards are gone and
    // the stepper is the whole quantity control rather than the top tier's.
    this.hasLadder = this.dataset.hasLadder !== 'false';

    // tier_display: table (2026-10-06). The stepper is the whole quantity
    // control from 1 up and the discount steps under it follow the quantity.
    // Liquid already turns the card row off in this mode (data-has-ladder is
    // false), so the stepper is never hidden; what changes here is its floor.
    this.tableMode = this.dataset.tierDisplay === 'table';
    this.tierRows = Array.from(this.querySelectorAll('[data-tier-row]')); this.otFixed = parseInt(this.dataset.otFixed, 10) || 0; this.subFixed = parseInt(this.dataset.subFixed, 10) || 0;

    this.freeDeliveryFrom = parseInt(this.dataset.freeDeliveryFrom, 10) || 0;
    this.subscribeFreeDelivery = this.dataset.subscribeFreeDelivery === 'true';
    this.hasPlans = this.dataset.hasPlans === 'true';
    // When each tier carries its own selling plan, the frequency control no
    // longer distinguishes them and is hidden — cadence is the same across
    // all three; only the recurring percentage differs.
    this.tierPlans = this.dataset.tierPlans === 'true';

    this.multipleFrequencies = undefined;

    // planSet BEFORE planId, and both before anything reads state.
    //
    // This used to be a single object literal calling #firstPlanId(), which
    // set this.state.planSet as a side effect — onto a temporary object that
    // the very assignment then replaced. So planSet was undefined on mount:
    // #syncFrequencies() saw no set, kept all nine frequencies and selected
    // the first plan, which is tier 1's. The page therefore loaded showing
    // three packs at tier 1's recurring rate until the shopper touched
    // something. Order of assignment, not logic, was the whole bug.
    const defaultCard = this.querySelector('[data-qty-input]:checked')?.closest('[data-qty-card]');
    this.state = {
      qty: parseInt(this.dataset.defaultQty, 10) || 1,
      mode: this.hasPlans ? 'subscribe' : 'onetime',
      planSet: this.tierPlans ? defaultCard?.dataset.plansForQty || '' : '',
      planId: ''
    };
    this.state.planId = this.#firstPlanId();

    // `change`, not `click` on the label. When a <label> wraps a visually
    // hidden radio, the browser's label-to-control forwarding happens as part
    // of the same click event, and its ordering relative to listeners added
    // here is not guaranteed — a click handler can still read the PREVIOUS
    // :checked state and write the wrong quantity to the form while the card
    // highlights correctly. (That exact bug is documented in the MO build's
    // picker, which patched around it by force-setting .checked first.)
    // `change` fires after the control has updated, so the race cannot occur,
    // and it also covers keyboard arrow-key navigation within the radio group
    // for free.
    this.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      if (target.matches('[data-qty-input]')) {
        this.state.qty = parseInt(target.value, 10) || 1;
        // Quantity-scoped rules: the tier carries its own selling plan group,
        // so changing pack size changes which rule is used. This is the only
        // thing pairing the two — Seal cannot scope a plan to a quantity.
        const tierSet = target.closest('[data-qty-card]')?.dataset.plansForQty;
        if (this.tierPlans && tierSet) {
          this.state.planSet = tierSet;
          this.#syncFrequencies();
        }
      } else if (target.matches('[data-plan-input]')) {
        this.state.mode = target.value; this.#fixQty();
      } else if (target.matches('[data-freq-select]')) {
        this.state.planId = target.value;
      } else {
        return;
      }
      this.#apply(true);
    });

    // Stepper: moves within the open-ended top tier only. Clamped to a
    // quantity that has a prepared price, so it can never show a figure the
    // server didn't calculate.
    this.addEventListener('click', (event) => {
      const step = event.target.closest('[data-qty-decrease], [data-qty-increase]');
      if (!step) return;
      const delta = step.hasAttribute('data-qty-increase') ? 1 : -1;
      const next = Math.min(this.maxQty, Math.max(this.#stepperFloor(), this.state.qty + delta));
      if (next === this.state.qty) return;
      this.state.qty = next;
      this.#apply(true);
    });

    this.#fixQty(); if (this.tierPlans) this.#syncFrequencies();
    this.#apply(true);

    // Seal Subscriptions keeps running its own init after DOM ready even with
    // its widget hidden via display:none, injecting and blanking its own
    // `selling_plan` input. A single write on load therefore isn't enough: it
    // is correct at the time, and then Seal's late-arriving input appears
    // after it. That was the "works only if I click something first" bug —
    // any click re-ran the write after Seal had finished, a fresh page load
    // never did.
    //
    // So the write is not a one-shot, it's an invariant re-asserted whenever
    // the form's controls change. The MO build tried a setTimeout guess at
    // Seal's init timing and found it unreliable on slow connections; an
    // observer needs no guess, because it reacts to the actual event.
    this.#observer = new MutationObserver(() => this.#writeForm());
    const form = this.#form();
    if (form) {
      this.#observer.observe(form, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value', 'name', 'disabled']
      });
    }

    // Seal's own signal that a widget now exists. This is the event the
    // MutationObserver above is approximating, and the proper hook to
    // re-apply our state onto a freshly created widget. Documented as firing
    // once per widget created — but it may only fire during Seal's initial
    // page-load pass, which a script running after load can't observe, so it
    // is wired as a bonus rather than relied on. The observer stays.
    document.addEventListener('sealsubs:subscription_widget_created', this.#onSealWidgetCreated);

    // Fires when Seal's own widget changes the plan. Our UI is the source of
    // truth, so a divergence means something else moved the selection and we
    // re-assert. Guarded by #writing so our own dispatch can't loop back
    // through this handler.
    document.addEventListener('sealsubs:selling_plan_changed', this.#onSealPlanChanged);

    // Belt and braces: the capture phase of `submit` runs before the form's
    // own target-phase handling, so this has the last word no matter what.
    // Kept — the sticky bar's button is driven by a click handler rather than
    // a real submit, and accelerated-checkout buttons read the form directly
    // without ever firing one.
    document.addEventListener('submit', this.#onSubmitCapture, true);
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.#onSubmitCapture, true);
    document.removeEventListener('sealsubs:subscription_widget_created', this.#onSealWidgetCreated);
    document.removeEventListener('sealsubs:selling_plan_changed', this.#onSealPlanChanged);
    this.#observer?.disconnect();
  }

  #onSealWidgetCreated = () => this.#writeForm();

  #onSealPlanChanged = (event) => {
    if (this.#writing) return;
    const reported = String(event?.detail?.selling_plan_id ?? '');
    if (reported === this.#currentPlanKey()) return;
    this.#writeForm();
  };

  /** @type {MutationObserver | undefined} */
  #observer;

  /** Guards against the observer reacting to its own writes. */
  #writing = false;

  #onSubmitCapture = (event) => {
    if (event.target === this.#form()) this.#writeForm();
  };

  /**
   * Rebuilds the delivery-frequency list for the currently selected pack size.
   *
   * Each pack size maps to its own Seal rule, and a rule can hold several
   * frequencies (1 / 2 / 3 months). So the frequency options are whatever
   * plans belong to THIS tier's rule — not every plan on the product, which
   * would let a shopper pick a 2-pack rule's frequency while buying 3.
   *
   * Keeps the shopper's chosen cadence across a pack change where the new rule
   * offers the same one, by matching on plan name; otherwise falls back to that
   * rule's first plan. The row hides itself when a rule has only one frequency,
   * because a one-option select is a decision nobody can make.
   */
  #syncFrequencies() {
    const select = this.querySelector('[data-freq-select]');
    // Matched on plan ids, not the group: Liquid's selling_plan_group_id is a
    // hash of the group's shape, and three structurally identical Seal rules
    // collapse to a single value — so it cannot tell the rules apart. Plan
    // names repeat across rules too. The ids are the only discriminator.
    const set = this.state.planSet;
    const available = set
      ? this.plans.filter((p) => set.includes(`,${p.id},`))
      : this.plans;
    if (!available.length) return;

    const previousName = this.#planName();
    const match = available.find((p) => p.name === previousName) || available[0];
    this.state.planId = match.id;

    if (select) {
      select.innerHTML = available
        .map(
          (p) =>
            `<option value="${p.id}"${p.id === match.id ? ' selected' : ''}>${escapeHtml(p.name)}</option>`
        )
        .join('');
    }

    const staticEl = this.querySelector('[data-freq-static]');
    if (staticEl) {
      staticEl.textContent = match.name;
      staticEl.dataset.sellingPlanId = match.id;
    }
    this.multipleFrequencies = available.length > 1;
  }

  #fixQty() { const f = this.state.mode === 'subscribe' ? this.subFixed : this.otFixed; if (!f) return; const input = this.querySelector(`[data-qty-input][value="${f}"]`); if (input) { input.checked = true; const set = input.closest('[data-qty-card]')?.dataset.plansForQty; if (this.tierPlans && set) { this.state.planSet = set; this.#syncFrequencies(); } } this.state.qty = f; } #firstPlanId() {
    // With quantity-scoped plans the default tier's own plan wins — the
    // frequency control is describing cadence, which is no longer what
    // distinguishes the plans.
    if (this.tierPlans) {
      const checked = this.querySelector('[data-qty-input]:checked');
      const set = this.state?.planSet || checked?.closest('[data-qty-card]')?.dataset.plansForQty;
      if (set) {
        const first = this.plans.find((p) => set.includes(`,${p.id},`));
        if (first) return first.id;
      }
    }
    const select = this.querySelector('[data-freq-select]');
    if (select) return select.value;
    const staticEl = this.querySelector('[data-freq-static]');
    return staticEl?.dataset.sellingPlanId || '';
  }

  #form() {
    // Prefer the deterministic id Horizon's buy-buttons block gives this exact
    // form over a looser action*="/cart/add" match, so a quick-add modal
    // elsewhere in the DOM can never be mistaken for the main product form.
    return (
      document.getElementById(this.dataset.formId) ||
      document.querySelector(`#shopify-section-${this.dataset.sectionId} form[action*="/cart/add"]`)
    );
  }

  /** Look up a prepared price row. Returns null when the combination is absent. */
  // The single lookup every consumer goes through, so the price line, the recap
  // and the sticky bar cannot disagree with each other.
  //
  // Rows whose discount lands on an exact half cent carry an `alt` block: two
  // candidate prices, both formatted server-side by Liquid. Bundler's function
  // computes the discount in binary floating point, and on an exact half the
  // float error alone decides the direction, which Liquid cannot observe
  // because its arithmetic round-trips through decimal strings. So we redo
  // Bundler's sum here, in the same order and the same types, and pick the
  // candidate it agrees with. No formatting, no arithmetic on money for
  // display, just a choice between two strings.
  #row(qty, plan) {
    const row = this.prices.find((entry) => entry.qty === qty && entry.plan === plan) || null;
    if (!row || !row.alt || !this.unitCents) return row;

    const disc = Math.round(((this.unitCents / 100) * qty) * (row.pct / 100) * 100);
    if (disc === row.alt.disc) {
      return { ...row, total: row.alt.total, unit: row.alt.unit, save: row.alt.save };
    }
    return row;
  }

  #currentPlanKey() {
    return this.state.mode === 'subscribe' ? this.state.planId : '';
  }

  /**
   * The cadence as a phrase rather than a bare plan name.
   *
   * Plan names are now cadences in their own right ("1 month", "2 months"),
   * which reads fine in a select next to "Deliver every" but not when dropped
   * into a sentence — "Then €101,97 1 month" is not English. Prefixing turns
   * every name into a phrase that works in both places, without parsing or
   * rewriting merchant-authored text.
   */
  #cadencePhrase() {
    const name = this.#planName();
    if (!name) return '';
    const every = this.dataset.lEvery;
    return every ? `${every} ${name}` : name;
  }

  #planName() {
    const select = this.querySelector('[data-freq-select]');
    if (select) return select.options[select.selectedIndex]?.textContent.trim() || '';
    return this.querySelector('[data-freq-static]')?.textContent.trim() || '';
  }

  #apply(writeToForm) {
    const qty = this.state.qty;
    const oneTime = this.#row(this.otFixed || qty, '');
    const subscribe = this.hasPlans ? this.#row(this.subFixed || qty, this.state.planId) : null;
    const multi = qty > 1;

    const subscribing = this.state.mode === 'subscribe';

    // --- purchase option cards -------------------------------------------
    // When an introductory offer is configured, the BIG number is what the
    // shopper pays today and the small line states what recurs. Showing only
    // the intro price would be the textbook subscription dark pattern, and
    // Forbrukertilsynet requires the renewal price pre-purchase anyway — so
    // the recurring figure is never more than one line away from the intro.
    if (subscribe) {
      // `total`/`unit`/`pct` are the FIRST order — what's charged today.
      // `recur*` is every renewal. They differ whenever the plan carries
      // cycle-based pricing (an introductory rate), and `hasIntro` says so.
      const intro = subscribe.hasIntro;
      this.#text('[data-plan-unit]', subscribe.unit);

      // The renewal price sits directly under the intro price, never further
      // away than one line. An intro rate shown alone is the textbook
      // subscription dark pattern, and Forbrukertilsynet requires the renewal
      // figure before purchase regardless.
      const secondary = intro
        ? `${this.dataset.lThen} ${subscribe.recurUnit} / ${this.dataset.perLabel}`
        : subscribe.total;
      this.#secondary('[data-plan-total]', secondary, intro || multi);

      const badge = this.querySelector('[data-plan-extra]');
      if (badge) {
        // Percentage, not kroner: comparable across products and pack sizes,
        // where "Save 76,50 kr" only means something once you know the base.
        // Leads on the first-order figure because that is the headline.
        badge.textContent = subscribe.pct > 0 ? `−${subscribe.pct}%` : '';
        badge.hidden = subscribe.pct <= 0;
      }
    }

    if (oneTime) {
      this.#text('[data-onetime-unit]', oneTime.unit);

      // Price-panel only (no purchase-option cards). The badge and the
      // strikethrough are one claim in two parts, so they appear together or
      // not at all: a struck price with no percentage beside it reads as an
      // error, and a percentage with nothing struck has no reference.
      const badge = this.querySelector('[data-onetime-badge]');
      const compare = this.querySelector('[data-onetime-compare]');
      const discounted = oneTime.pct > 0;
      if (badge) {
        badge.textContent = discounted ? `−${oneTime.pct}%` : '';
        badge.hidden = !discounted;
      }
      // The reference is the full price of ONE unit (the one-unit row's
      // gross), struck beside the per-unit price it is compared with
      // (2026-10-09). It used to be the gross for the selected quantity, which
      // at 3 read "€17,49 / pack, was €104,97". The quantity's total has its
      // own line below.
      if (compare) {
        const single = this.#row(1, '');
        compare.textContent = single ? single.gross : oneTime.gross;
        compare.hidden = !discounted;
      }

      // Labelled "total" rather than left as a bare number. The Subscribe card
      // opposite it shows a rate ("Then £25.28 / pack"), so an unlabelled
      // figure in the same position invited reading this as a rate too.
      this.#secondary(
        '[data-onetime-total]',
        multi ? `${oneTime.total} ${this.dataset.lTotalSuffix}` : '',
        multi
      );
    }

    // --- quantity ladder, per purchase option -----------------------------
    // The pack ladder is not one ladder. Buying once uses Bundler's quantity
    // break; subscribing uses the plans' own ongoing percentages, which are a
    // different set of numbers. Subscribe is preselected, so showing the
    // one-time ladder would headline percentages for an option the shopper
    // isn't on. The hint wording swaps too, because "lower price each" doesn't
    // say whether the saving repeats.
    for (const card of this.querySelectorAll('[data-qty-card]')) {
      const cardQty = parseInt(card.dataset.qty, 10) || 1;
      const badge = card.querySelector('[data-qty-badge]');
      if (!badge) continue;

      let shown = 0;
      if (subscribing) {
        // That tier's own plan, then its recurring percentage.
        const cardSet = card.dataset.plansForQty;
        const tierPlan = cardSet
          ? this.plans.find((p) => cardSet.includes(`,${p.id},`))
          : this.plans[0];
        const row = tierPlan ? this.#row(cardQty, tierPlan.id) : null;
        shown = row ? row.recurPct : 0;
      } else {
        const row = this.#row(cardQty, '');
        shown = row ? row.pct : 0;
      }

      // Bare percentage. The row's hint above already says whether this ladder
      // is the ongoing one or the one-time one, so repeating the word on all
      // three pills was the same word three times — and at mobile width it
      // pushed each pill past its card and into the ribbon opposite.
      badge.textContent = `−${shown}%`;
      badge.hidden = shown <= 0;
    }

    // The ladder still owns the tier; the stepper only moves inside the one
    // tier with no upper bound, so it stays hidden until that tier is selected.
    const stepper = this.querySelector('[data-qty-stepper]');
    if (stepper) {
      // With no ladder the stepper is the whole control and never hides. With
      // one, it belongs to the open-ended top tier only. Same decision the
      // server made, from the same flag, so the two cannot disagree.
      stepper.hidden = this.hasLadder && qty < this.topTierQty;
      const output = stepper.querySelector('[data-qty-output]');
      if (output) output.textContent = String(qty);
      const minus = stepper.querySelector('[data-qty-decrease]');
      const plus = stepper.querySelector('[data-qty-increase]');
      if (minus) minus.disabled = qty <= this.#stepperFloor();
      if (plus) plus.disabled = qty >= this.maxQty;
    }

    const hint = this.querySelector('[data-qty-hint]');
    if (hint) {
      const wording = subscribing ? hint.dataset.hintSubscribe : hint.dataset.hintOnetime;
      if (wording) hint.textContent = wording;
    }

    // --- free-delivery ribbons -------------------------------------------
    // Purchase-option cards only. Delivery is a property of how you buy, not
    // of how many you buy, and putting it on both rows read as two offers.
    const oneTimeRibbon = this.querySelector('[data-onetime-ribbon]');
    if (oneTimeRibbon) {
      const earned = this.freeDeliveryFrom > 0 && qty >= this.freeDeliveryFrom;
      oneTimeRibbon.hidden = !earned;
      oneTimeRibbon.closest('[data-plan-card]')?.classList.toggle('hl-pack__card--ribboned', earned);
    }

    // The note under the ladder, which only exists when there are no
    // purchase-option cards to carry the ribbon. Both strings come from the
    // markup, so the script still holds no user-facing copy and translation
    // stays a theme-settings job rather than a code change.
    const deliveryNote = this.querySelector('[data-delivery-note]');
    if (deliveryNote) {
      const earned = this.freeDeliveryFrom > 0 && qty >= this.freeDeliveryFrom;
      const text = earned ? deliveryNote.dataset.noteEarned : deliveryNote.dataset.noteUnearned;
      const target = deliveryNote.querySelector('[data-delivery-text]');
      if (target && text && target.textContent !== text) target.textContent = text;
      deliveryNote.classList.toggle('is-earned', earned);
    }

    // --- frequency row ----------------------------------------------------
    // Nothing to schedule on a one-time purchase.
    const freqRow = this.querySelector('[data-freq-row]');
    // Hidden on one-time (nothing to schedule) and when the selected rule
    // offers a single frequency. Not hidden merely because tier plans are in
    // use — a rule with 1/2/3-month options still needs the control.
    if (freqRow) {
      const onlyOne = this.tierPlans && this.multipleFrequencies === false;
      freqRow.hidden = !subscribing || onlyOne;
    }

    // --- tier table (table mode only) ---------------------------------------
    this.#renderTierTable(qty);

    // --- recap ------------------------------------------------------------
    this.#renderRecap(subscribing ? subscribe : oneTime, subscribing);

    if (writeToForm) this.#writeForm();
    this.#announce(subscribing ? subscribe : oneTime, subscribing);
  }

  /**
   * The stepper's lowest value. With tier cards it only moves inside the
   * open-ended top tier, so its floor is that tier; in table mode it is the
   * whole quantity control and starts at 1. With a single tier the top tier
   * IS 1, so that case needs no branch of its own.
   */
  #stepperFloor() {
    return this.tableMode ? 1 : this.topTierQty;
  }

  /**
   * Discount steps (table mode only, simplified 2026-10-09 to Betakaroten
   * Gold's live version). The step that applies to the selected quantity is
   * the highest tier at or below it. The nudge line, in order:
   *   - top tier reached: best-price line (%SAVE%)
   *   - the next tier costs no more than the current quantity: "N for the
   *     price of C" (%N%, %C%)
   *   - otherwise point at the top tier (%Q%, %PCT%)
   * Totals come from the prepared rows via #row, so Bundler's half-cent
   * rounding is already resolved; both strings share one money format, so
   * their digits compare as cents.
   */
  #renderTierTable(qty) {
    if (!this.tableMode || this.tierRows.length === 0) return;
    const cents = (s) => parseInt(String(s || '').replace(/[^0-9]/g, ''), 10) || 0;
    let active = null;
    let nextQty = 0;
    let topQty = 0;
    let topPct = 0;
    for (const tierRow of this.tierRows) {
      const tierQty = parseInt(tierRow.dataset.tierQty, 10) || 1;
      if (tierQty <= qty) active = tierRow;
      else if (!nextQty) nextQty = tierQty;
      if (tierQty > topQty) {
        topQty = tierQty;
        topPct = parseInt(tierRow.dataset.tierPct, 10) || 0;
      }
    }
    for (const tierRow of this.tierRows) tierRow.toggleAttribute('data-active', tierRow === active);

    const hint = this.querySelector('[data-tier-hint]');
    const table = this.querySelector('[data-tier-table]');
    if (!hint || !table) return;
    const current = this.#row(qty, '');
    if (qty >= topQty) {
      hint.textContent = (table.dataset.bestHint || '').replace('%SAVE%', current ? current.save : '');
      return;
    }
    const nextRow = nextQty ? this.#row(nextQty, '') : null;
    if (current && nextRow && cents(nextRow.total) <= cents(current.total)) {
      hint.textContent = (table.dataset.nudgeFree || '').replace('%N%', String(nextQty)).replace('%C%', String(qty));
    } else {
      hint.textContent = (table.dataset.nudgeTop || '').replace('%Q%', String(topQty)).replace('%PCT%', `−${topPct}%`);
    }
  }

  #renderRecap(row, subscribing) {
    const list = this.querySelector('[data-recap-list]');
    if (!list || !row) return;

    const qty = this.state.qty;
    const unitNoun = qty === 1 ? this.dataset.unitSingular : this.dataset.unitPlural;
    const d = this.dataset;
    const intro = subscribing && row.hasIntro;

    // The recap describes the FIRST order, because that is what the "You pay
    // today" figure beneath it refers to. When an introductory rate is in play
    // the two discounts are stated as separate rows rather than one blended
    // number — a shopper comparing packs needs to know which saving continues
    // and which is a one-off, and "57% off" collapsed into a single line hides
    // exactly that.
    const rows = [
      [d.lQuantity, `${qty} ${unitNoun}`],
      [`${d.lPricePer} ${d.perLabel}`, row.unit]
    ];

    if (intro) {
      // PRICES here, not savings. Labelled "First order" and "Then every
      // order", a money value reads as what that order costs — so showing the
      // saving there said £60.34 for an order that actually costs £44.63.
      // The percentage carries the saving, and the two rows now agree with
      // "You pay today" beneath them and with the renewal sentence.
      // The percentage is parenthetical, so it is omitted at zero rather than
      // printed as "(−0%)". This is reachable from 2026-09-17: the recurring
      // ladder now equals the one-time ladder, whose first tier is 0%, so a
      // 1-pack subscription renews at the shelf price. The row still shows the
      // renewal PRICE, which is what has to be disclosed — only the redundant
      // "no discount" annotation goes.
      const pctSuffix = (value) => (value > 0 ? ` (−${value}%)` : '');
      rows.push([d.lFirstOrder, `<b>${escapeHtml(row.total)}</b>${pctSuffix(row.pct)}`]);
      rows.push([d.lThenEvery, `<b>${escapeHtml(row.recurTotal)}</b>${pctSuffix(row.recurPct)}`]);
    } else {
      rows.push([d.lYouSave, `<b>${escapeHtml(row.save)}</b> (${row.pct}%)`]);
    }

    const cadence = this.#cadencePhrase();
    rows.push([
      d.lDelivery,
      subscribing ? cadence.charAt(0).toUpperCase() + cadence.slice(1) : d.lOnce
    ]);

    list.innerHTML = rows
      .map(([term, value]) => `<dt>${escapeHtml(term)}</dt><dd>${value}</dd>`)
      .join('');

    this.#text('[data-recap-pay]', row.total);

    const then = this.querySelector('[data-recap-then]');
    if (then) {
      // The renewal price, its own percentage and the cadence must all be
      // visible before purchase (Forbrukertilsynet, dev guide §6.3) — and
      // doubly so when an introductory rate means the first charge is not the
      // one that repeats.
      // Two sentences, in the order the shopper reads the numbers above them:
      // first why today's figure is lower than it will be, then what actually
      // recurs. The intro rate is named HERE rather than on the pill — it's
      // the one number that doesn't repeat, and this is where both prices are
      // already side by side.
      //
      // No cancellation terms: those sit under the add-to-cart button, and
      // saying them twice in one screen reads as small print rather than
      // reassurance.
      const price = intro ? row.recurTotal : row.total;
      const pct = intro ? row.recurPct : row.pct;
      const introSentence = intro
        ? `<b>−${row.pct}%</b> ${escapeHtml(d.lFirstOrderNote)}. `
        : '';
      const recurring = `${escapeHtml(d.lThen)} <b>${escapeHtml(price)}</b> ${escapeHtml(this.#cadencePhrase())}`;
      then.innerHTML = subscribing
        ? `${introSentence}${recurring}${pct > 0 ? ` (−${pct}%)` : ''}.`
        : escapeHtml(d.onetimeNote || '');
    }
  }

  #writeForm() {
    const form = this.#form();
    if (!form || this.#writing) return;
    this.#writing = true;
    try {
      this.#writeFormInner(form);
    } finally {
      // Cleared on a microtask, after the observer has processed this batch,
      // so our own DOM writes can't trigger another pass.
      queueMicrotask(() => { this.#writing = false; });
    }
  }

  #writeFormInner(form) {

    // Horizon's real quantity input, driven rather than duplicated so its
    // quantity-rule validation still applies. The disabled hidden fallback in
    // the block markup is only enabled if that input is genuinely absent —
    // otherwise two fields named `quantity` would both submit.
    const real = form.querySelector('input[name="quantity"]');
    const fallback = this.querySelector('[data-qty-fallback]');
    if (real) {
      if (String(real.value) !== String(this.state.qty)) {
        real.value = String(this.state.qty);
        real.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (fallback) fallback.disabled = true;
    } else if (fallback) {
      fallback.disabled = false;
      fallback.value = String(this.state.qty);
    }

    this.#writeSellingPlan(form);
  }

  /**
   * Writes the selling plan, and — more importantly — guarantees the form
   * carries exactly ONE authoritative `selling_plan` value.
   *
   * Seal Subscriptions injects its own `input[name="selling_plan"]` into the
   * product form. Horizon submits via `new FormData(form)`, which collects
   * every element whose form owner is that form — including ours, associated
   * from outside via the `form` attribute. Two inputs of the same name both
   * land in the body, and the last one wins. Seal's sits later in the DOM and
   * is normally empty, so a shopper who chose Subscribe got a one-time line:
   * our value was written correctly and then overridden by an empty duplicate.
   *
   * Two steps, in this order, and the order matters.
   *
   * 1. WRITE THE INPUTS DIRECTLY. Our own hidden input plus any Seal has
   *    injected all get the same value, so the form is correct on its own
   *    merits. These are native Shopify selling plans; if Seal is ever
   *    uninstalled or its script fails to load, add-to-cart still works.
   *    Because every input carries the *same* value, the duplicate-name
   *    problem that previously turned subscriptions into one-time purchases
   *    can't reoccur — last-one-wins no longer changes the answer.
   *
   * 2. TELL SEAL, through its documented event. Writing Seal's input directly
   *    changes a value Seal doesn't know about: the selection isn't part of
   *    its state, so its next init pass is free to reset it. Dispatching
   *    `sealsubs:change_selected_selling_plan_id` makes Seal set its own
   *    input, which leaves its state and the form agreeing and nothing to
   *    clobber. An earlier version of this file fought that by disabling
   *    Seal's input instead — which worked, but left Seal believing the
   *    shopper had chosen one-time.
   */
  #writeSellingPlan(form) {
    const value = this.#currentPlanKey();

    const own = this.querySelector('[data-selling-plan-input]');
    if (own) {
      own.disabled = false;
      own.value = value;
    }
    for (const el of Array.from(form.elements)) {
      if (el.name === 'selling_plan' && el !== own) el.value = value;
    }

    this.#setSellingPlanViaSeal(value);
  }

  /**
   * Drives Seal's own selection. An empty string selects one-time.
   *
   * The widgets are re-queried on EVERY call, never cached: Seal binds its
   * listeners to the widget elements when it creates them, so anything that
   * re-renders them (`SealSubs.hardRefresh()`, a variant change) rebinds to
   * new nodes and a held reference silently stops working — an intermittent
   * bug with no error to follow. There can also be more than one widget on a
   * page, so all of them are dispatched to.
   *
   * Works with the widget hidden: `.sealsubs-target-element { display: none }`
   * in sections/pdp-buybox.liquid does not disable this API, so the custom UI
   * stays the only thing the shopper sees.
   *
   * @returns {boolean} whether any widget was found to dispatch to.
   */
  #setSellingPlanViaSeal(sellingPlanId) {
    const widgets = document.querySelectorAll('.sealsubs-target-element');
    if (!widgets.length) return false;

    for (const widget of widgets) {
      widget.dispatchEvent(
        new CustomEvent('sealsubs:change_selected_selling_plan_id', {
          detail: { selling_plan_id: sellingPlanId || '' }
        })
      );
    }
    return true;
  }

  /**
   * Keeps Horizon's sticky add-to-cart bar in step. That bar is Horizon's own
   * component and has no concept of selling plans or pack size; the existing
   * listener in assets/pdp-buybox.js writes whatever label this event carries
   * into [data-sticky-plan-label]. Emitting the same event name the retired
   * <hl-plan-selector> used means the sticky bar needed no changes at all.
   */
  #announce(row, subscribing) {
    if (!row) return;
    const qty = this.state.qty;
    const unitNoun = qty === 1 ? this.dataset.unitSingular : this.dataset.unitPlural;
    const cadence = subscribing ? this.#cadencePhrase() : this.dataset.lOnce;
    this.dispatchEvent(
      new CustomEvent('hl:plan-changed', {
        bubbles: true,
        detail: {
          label: `${qty} ${unitNoun} · ${cadence}`,
          price: row.total,
          quantity: qty,
          sellingPlanId: this.#currentPlanKey()
        }
      })
    );
  }

  #text(selector, value) {
    const el = this.querySelector(selector);
    if (el) el.textContent = value;
  }

  #secondary(selector, value, visible) {
    const el = this.querySelector(selector);
    if (!el) return;
    el.textContent = value;
    el.hidden = !visible;
  }
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
  });
}

if (!customElements.get('hl-pack-picker')) {
  customElements.define('hl-pack-picker', HlPackPicker);
}
